import React, { FC, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { observer } from "mobx-react";
import buttonStyles from "ui/Button/Button.module.scss";
import CircleIcon from "common/CircleIcon/CircleIcon";
import Price from "common/Price/Price";
import Icon from "assets/icons/cart.svg";
import ZCBonuses from "assets/icons/zc-bonuses.svg";
import { useStore } from "store";
import { pluralize } from "helpers/text";
import { cn } from "helpers/style";
import { withTrailSlash } from "helpers/url";
import { NAVIGATION_HOVER_TIMEOUT } from "constants/variables";
import { ROUTE_CART } from "constants/routes";
import ItemPreview from "./components/ItemPreview/ItemPreview";
import styles from "./Cart.module.scss";

const Dropdown = dynamic(() => import("common/Dropdown/Dropdown"));

const Cart: FC = () => {
  const { cart, user } = useStore();
  const { pathname } = useRouter();
  const [submitting, setSubmitting] = useState(false);
  const [cartIsOpened, updateCartOpened] = useState(false);
  const isEmpty = cart.count <= 0;
  const isCartPage = pathname.includes(ROUTE_CART);

  function createRemoveHandler(item) {
    return async function () {
      await cart.remove(item.product, { priceTypeID: item.priceTypeID });
    };
  }

  function createCountChangeHandler(item) {
    return async function (quantity) {
      setSubmitting(true);
      try {
        await cart.setItemCount(item, quantity);
      } finally {
        setSubmitting(false);
      }
    };
  }

  const bonuses = cart.totalBonuses;
  const bonusesText = pluralize(bonuses, "балл", "балла", "баллов");

  let timer = null;
  useEffect(() => {
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onHoverCart = opened => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      updateCartOpened(opened);
    }, NAVIGATION_HOVER_TIMEOUT);
  };

  if (isCartPage) {
    return (
      <div className={styles.cart}>
        <CircleIcon
          icon={<Icon />}
          counter={cart.count}
          className={styles.cartIcon}
        />
      </div>
    );
  }

  return (
    <div
      className={cn([styles.cart, cartIsOpened && styles.opened])}
      onMouseOver={() => onHoverCart(true)}
      onMouseLeave={() => onHoverCart(false)}
    >
      <Dropdown
        heading={
          // eslint-disable-next-line @next/next/no-html-link-for-pages
          <a aria-label="Корзина" href={withTrailSlash(ROUTE_CART)}>
            <CircleIcon
              icon={<Icon />}
              counter={cart.count}
              className={styles.cartIcon}
            />
          </a>
        }
        position="bottom-end"
        width={!isEmpty ? "510px" : "initial"}
        custom={isEmpty}
        className={styles.cartDropdown}
      >
        {!isEmpty ? (
          <div className={styles.cartBody}>
            <h5 className={styles.cartHeading}>Корзина</h5>
            <div className={styles.cartList}>
              {cart.flattenGroups.map(item => {
                return (
                  <ItemPreview
                    key={`product-${item.product.id}-${item.priceTypeID}`}
                    item={item}
                    onRemove={createRemoveHandler(item)}
                    onChangeCount={createCountChangeHandler(item)}
                    submitting={submitting}
                    isEmployeeMode={user.isEmployeeMode}
                  />
                );
              })}
            </div>
            <div className={styles.cartFooter}>
              <div className={styles.cartFooterScore}>
                <div className={styles.cartFooterScoreTitle}>Итого</div>
                <Price
                  className={styles.cartFooterScorePrice}
                  price={cart.priceTotal}
                  discount={cart.discountTotal}
                  priceOld={cart.priceOldTotal}
                  withoutPrefix
                />
                {bonuses >= 1 && (
                  <div className={styles.cartFooterBonuses}>
                    +{bonuses} {bonusesText}
                    <ZCBonuses />
                  </div>
                )}
              </div>
              {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
              <a href={withTrailSlash(ROUTE_CART)}>
                <button
                  className={cn([
                    buttonStyles.button,
                    buttonStyles.buttonOrange,
                    buttonStyles.buttonFluid,
                    styles.cartFooterButton,
                  ])}
                >
                  Перейти в корзину
                </button>
              </a>
            </div>
          </div>
        ) : (
          <div className={styles.cartEmpty}>Корзина пуста</div>
        )}
      </Dropdown>
    </div>
  );
};

export default observer(Cart);
